import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Search,
  LayoutDashboard,
  Shield,
  AlertTriangle,
  Crosshair,
  CheckSquare,
  Box,
  Truck,
  FileSearch,
  Settings,
  ArrowRight,
  Command,
  Loader2,
  FileText,
  Target,
  PiggyBank,
} from 'lucide-react';

const TYPE_CONFIG = {
  vulnerability: { icon: AlertTriangle, bg: 'bg-orange-100', text: 'text-orange-600', label: 'Kwetsbaarheid' },
  risk: { icon: Shield, bg: 'bg-purple-100', text: 'text-purple-600', label: 'Risico' },
  measure: { icon: CheckSquare, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Maatregel' },
  asset: { icon: Box, bg: 'bg-green-100', text: 'text-green-600', label: 'Asset' },
  threat: { icon: Crosshair, bg: 'bg-red-100', text: 'text-red-600', label: 'Dreiging' },
  policy: { icon: FileText, bg: 'bg-indigo-100', text: 'text-indigo-600', label: 'Beleid' },
  page: { icon: LayoutDashboard, bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Pagina' },
};

const GlobalSearch = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const quickNav = [
    { type: 'page', name: 'Home', href: '/dashboard', icon: LayoutDashboard, group: 'Navigatie' },
    { type: 'page', name: 'Nulmeting', href: '/beoordelen/nulmeting', icon: FileSearch, group: 'Beoordelen' },
    { type: 'page', name: 'Volwassenheid', href: '/beoordelen/volwassenheid', icon: FileSearch, group: 'Beoordelen' },
    { type: 'page', name: "Risico's", href: '/risicos/overzicht', icon: Shield, group: "Risico's" },
    { type: 'page', name: 'Kwetsbaarheden', href: '/risicos/kwetsbaarheden', icon: AlertTriangle, group: "Risico's" },
    { type: 'page', name: 'Dreigingen', href: '/risicos/dreigingen', icon: Crosshair, group: "Risico's" },
    { type: 'page', name: 'Systemen', href: '/risicos/systemen', icon: Box, group: "Risico's" },
    { type: 'page', name: 'Maatregelen', href: '/verbeteren/maatregelen', icon: CheckSquare, group: 'Verbeteren' },
    { type: 'page', name: 'Strategie', href: '/verbeteren/strategie', icon: Target, group: 'Verbeteren' },
    { type: 'page', name: 'Financieel', href: '/verbeteren/financieel', icon: PiggyBank, group: 'Verbeteren' },
    { type: 'page', name: 'Leveranciers', href: '/verbeteren/leveranciers', icon: Truck, group: 'Verbeteren' },
    { type: 'page', name: 'Organisatie', href: '/organisatie/gegevens', icon: Settings, group: 'Organisatie' },
    { type: 'page', name: 'Mensen & teams', href: '/organisatie/mensen', icon: Settings, group: 'Organisatie' },
    { type: 'page', name: 'Verantwoordelijkheden', href: '/organisatie/verantwoordelijkheden', icon: Settings, group: 'Organisatie' },
  ];

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(`/ggrc/search?q=${encodeURIComponent(searchQuery)}`);
      const backendResults = (res.data.results || []).map(r => ({
        ...r,
        icon: TYPE_CONFIG[r.type]?.icon || FileSearch,
      }));

      // Also filter quickNav for page matches
      const searchLower = searchQuery.toLowerCase();
      const pageMatches = quickNav
        .filter(p => p.name.toLowerCase().includes(searchLower))
        .map(p => ({ ...p, id: p.href }));

      setResults([...pageMatches, ...backendResults]);
    } catch {
      // Fallback to client-side quick nav filter
      const searchLower = searchQuery.toLowerCase();
      setResults(quickNav.filter(p => p.name.toLowerCase().includes(searchLower)));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => { performSearch(query); }, 300);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setSelectedIndex(0); }
  }, [open]);

  const handleKeyDown = (e) => {
    const items = query ? results : quickNav;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = items[selectedIndex];
      if (selected) { navigate(selected.href); onOpenChange(false); }
    }
  };

  const handleSelect = (item) => { navigate(item.href); onOpenChange(false); };

  const getSeverityColor = (severity) => {
    const colors = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' };
    return colors[severity] || 'bg-neutral-100 text-neutral-700';
  };

  const displayItems = query ? results : quickNav;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Zoeken</DialogTitle>
        <DialogDescription className="sr-only">Zoek in risico's, maatregelen, assets, beleid en navigeer snel naar pagina's</DialogDescription>

        <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200">
          <Search className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Zoek in risico's, maatregelen, assets, beleid..."
            className="border-0 p-0 h-auto text-base focus-visible:ring-0"
            autoFocus
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
          <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border border-neutral-200 bg-neutral-100 px-1.5 text-[10px] font-medium text-neutral-500">ESC</kbd>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2" data-testid="global-search-results">
          {!query && (() => {
            const groups = {};
            quickNav.forEach(item => {
              const g = item.group || 'Overig';
              if (!groups[g]) groups[g] = [];
              groups[g].push(item);
            });
            return Object.entries(groups).map(([group, items]) => (
              <div key={group}>
                <div className="px-2 py-1.5 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">{group}</div>
                {items.map((item, i) => {
                  const globalIdx = quickNav.indexOf(item);
                  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.page;
                  const Icon = item.icon || cfg.icon;
                  const isSelected = globalIdx === selectedIndex;
                  return (
                    <button key={item.href} onClick={() => handleSelect(item)} onMouseEnter={() => setSelectedIndex(globalIdx)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isSelected ? 'bg-[#F3F4F6]' : 'hover:bg-[#F9FAFB]'}`}
                      data-testid={`search-result-${globalIdx}`}>
                      <Icon className="h-4 w-4 text-[#6B7280]" strokeWidth={1.5} />
                      <span className="text-sm text-[#111827]">{item.name}</span>
                      {isSelected && <ArrowRight className="h-3.5 w-3.5 text-[#9CA3AF] ml-auto" strokeWidth={1.5} />}
                    </button>
                  );
                })}
              </div>
            ));
          })()}

          {query && results.length === 0 && !loading && (
            <div className="py-8 text-center text-neutral-500">Geen resultaten voor "{query}"</div>
          )}

          {displayItems.map((item, index) => {
            const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.page;
            const Icon = item.icon || cfg.icon;
            const isSelected = index === selectedIndex;

            return (
              <button
                key={`${item.type}-${item.id || item.href}`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  isSelected ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                }`}
                data-testid={`search-result-${index}`}
              >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <Icon className={`h-4 w-4 ${cfg.text}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{item.name}</p>
                  <div className="flex items-center gap-2">
                    {item.subtitle && <p className="text-xs text-neutral-500 truncate">{item.subtitle}</p>}
                    {item.type !== 'page' && (
                      <span className="text-[10px] text-neutral-400 uppercase">{cfg.label}</span>
                    )}
                  </div>
                </div>
                {item.severity && (
                  <Badge className={`${getSeverityColor(item.severity)} border-transparent text-xs capitalize`}>{item.severity}</Badge>
                )}
                {item.status && !item.severity && (
                  <Badge className="bg-neutral-100 text-neutral-600 border-transparent text-xs capitalize">{item.status}</Badge>
                )}
                {isSelected && <ArrowRight className="h-4 w-4 text-neutral-400 flex-shrink-0" strokeWidth={1.5} />}
              </button>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-neutral-200">&#8593;</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-neutral-200">&#8595;</kbd>
                navigeren
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-neutral-200">&#8629;</kbd>
                openen
              </span>
            </div>
            <span className="flex items-center gap-1"><Command className="h-3 w-3" />K om te zoeken</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
